import { AnchorGroup } from "@/components/anchors-navigation";
import FilterButton from "@/components/filtering/Button";
import ClassificationFilter from "@/components/filtering/Classification";
import DisciplineFilter from "@/components/filtering/Discipline";
import SoftwareFilter from "@/components/filtering/Software";
import UseCaseFilter from "@/components/filtering/UseCase";
import VersionFilter from "@/components/filtering/Version";
import { useDataStore, useFilterStore } from "@/stores";
import useExportStore from "@/stores/useExportStore";
import { isStructuredExportAvailable } from "@/utils/VersionUtils";
import { Element, useEditor, useNode } from "@craftjs/core";
import clsx from "clsx";
import { useMemo } from "react";
import StepHeading from "./components/Heading";
import StepParagraph from "./components/Paragraph";
import StepTextContainer from "./components/TextContainer";

const Step1Content = () => {
    const classifications = useDataStore((state) => state.classifications);
    const versions = useDataStore((state) => state.versions);
    const activeVersion = useFilterStore((state) => state.activeModalVersion);
    const createExport = useExportStore((state) => state.createExport);

    const { enabled } = useEditor((state) => ({
        enabled: state.options.enabled,
    }));

    const {
        connectors: { connect },
    } = useNode();

    const hasClassifications = useMemo(() => {
        if (!classifications || !activeVersion) return false;

        if (!classifications[activeVersion]) return false;

        return classifications[activeVersion].length > 0;
    }, [classifications, activeVersion]);

    const showStructuredExport = useMemo(() => {
        return isStructuredExportAvailable(versions.find((x) => x.id === activeVersion));
    }, [versions, activeVersion]);

    return (
        <div ref={(ref) => connect(ref!)}>
            <main className="container relative mx-auto 2xl:px-[108px] z-[1] pb-[160px]">
                <div className="relative grid grid-cols-[auto_64px_1fr] gap-13 gap-y-50 mt-36">
                    <AnchorGroup>
                        <FilterButton />
                    </AnchorGroup>

                    <div className="relative w-full col-start-2">
                        <span id="lead-0" className="absolute -top-36 w-16 h-1" />
                        <span
                            id="lead-1"
                            className="absolute -right-[0.5px] rounded-full bg-primary-200 text-primary-500 w-16 h-16 text-[35px] inline-flex items-center justify-center font-bold z-1 select-none"
                        >
                            A
                        </span>
                    </div>
                    <div>
                        <Element
                            id="heading-a"
                            is={StepHeading}
                            text={`Sélectionner la/les <span class="text-primary-500">discipline(s)</span>`}
                        />
                        <Element id="content-a" is={StepTextContainer} canvas>
                            <Element
                                id="paragraph-a-1"
                                is={StepParagraph}
                                text={`Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec quam lacus, tristique at <a href="#">nulla et, imperdiet molestie nibh</a>. Vestibulum mollis viverra odio vitae sodales.`}
                            />
                        </Element>

                        <div
                            className={clsx([
                                "grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 mt-10.5 gap-1 xl:gap-4",
                                enabled && "pointer-events-none",
                            ])}
                        >
                            <DisciplineFilter />
                        </div>
                    </div>

                    <div className="relative w-full  col-start-2">
                        <span
                            id="lead-3"
                            className="absolute -right-[0.5px] rounded-full bg-primary-200 text-primary-500 w-16 h-16 text-[35px] inline-flex items-center justify-center font-bold z-1 select-none"
                        >
                            B
                        </span>
                    </div>
                    <div>
                        <Element
                            id="heading-b"
                            is={StepHeading}
                            text={`Sélectionner la <span class="text-primary-500">version</span>`}
                        />
                        <Element id="content-b" is={StepTextContainer} canvas>
                            <Element
                                id="paragraph-b-1"
                                is={StepParagraph}
                                text={`Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec quam lacus, tristique at <a href="#" class="font-bold text-primary-500 hover:text-primary-600 hover:underline">nulla et, imperdiet molestie nibh</a>. Vestibulum mollis viverra odio vitae sodales.`}
                            />
                        </Element>

                        <div
                            className={clsx([
                                "grid grid-cols-2 xl:grid-cols-4 mt-10.5 gap-1 xl:gap-4",
                                enabled && "pointer-events-none",
                            ])}
                        >
                            <VersionFilter />
                        </div>
                    </div>

                    <div className="relative w-full  col-start-2">
                        <span
                            id="lead-2"
                            className="absolute -right-[0.5px] rounded-full bg-primary-200 text-primary-500 w-16 h-16 text-[35px] inline-flex items-center justify-center font-bold z-1 select-none"
                        >
                            C
                        </span>
                    </div>
                    <div>
                        <Element
                            id="heading-c"
                            is={StepHeading}
                            text={`Sélectionner le(s) <span class="text-primary-500">cas d'usage(s)</span>`}
                        />
                        <Element id="content-c" is={StepTextContainer} canvas>
                            <Element
                                id="paragraph-c-1"
                                is={StepParagraph}
                                text={`Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec quam lacus, tristique at <a href="#" class="font-bold text-primary-500 hover:text-primary-600 hover:underline">nulla et, imperdiet molestie nibh</a>. Vestibulum mollis viverra odio vitae sodales.`}
                            />
                        </Element>

                        <div
                            className={clsx([
                                "grid grid-cols-1 xl:grid-cols-2 mt-2 gap-1 xl:gap-4",
                                enabled && "pointer-events-none",
                            ])}
                        >
                            <UseCaseFilter />
                        </div>
                    </div>

                    <div className="relative w-full  col-start-2">
                        <span
                            id="lead-4"
                            className="absolute -right-[0.5px] rounded-full bg-primary-200 text-primary-500 w-16 h-16 text-[35px] inline-flex items-center justify-center font-bold z-1 select-none"
                        >
                            D
                        </span>
                    </div>
                    <div>
                        <Element
                            id="heading-d"
                            is={StepHeading}
                            text={`Sélectionner la/les <span class="text-primary-500">version(s) IFC</span>`}
                        />
                        <Element id="content-d" is={StepTextContainer} canvas>
                            <Element
                                id="paragraph-d-1"
                                is={StepParagraph}
                                text={`Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec quam lacus, tristique at <a href="#">nulla et, imperdiet molestie nibh</a>. Vestibulum mollis viverra odio vitae sodales.`}
                            />
                        </Element>

                        <div
                            className={clsx([
                                "grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 mt-10.5 gap-1 xl:gap-4",
                                enabled && "pointer-events-none",
                            ])}
                        >
                            <SoftwareFilter ifc />
                        </div>
                    </div>

                    <div className="relative w-full  col-start-2">
                        <span
                            id="lead-5"
                            className="absolute -right-[0.5px] rounded-full bg-primary-200 text-primary-500 w-16 h-16 text-[35px] inline-flex items-center justify-center font-bold z-1 select-none"
                        >
                            E
                        </span>
                    </div>
                    <div>
                        <Element
                            id="heading-e"
                            is={StepHeading}
                            text={`Sélectionner le(s) <span class="text-primary-500">logiciel(s)</span>`}
                        />
                        <Element id="content-e" is={StepTextContainer} canvas>
                            <Element
                                id="paragraph-e-1"
                                is={StepParagraph}
                                text={`Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec quam lacus, tristique at <a href="#">nulla et, imperdiet molestie nibh</a>. Vestibulum mollis viverra odio vitae sodales.`}
                            />
                        </Element>

                        <div
                            className={clsx([
                                "grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 mt-10.5 gap-1 xl:gap-4",
                                enabled && "pointer-events-none",
                            ])}
                        >
                            <SoftwareFilter />
                        </div>
                    </div>

                    {hasClassifications && (
                        <>
                            <div className="relative w-full  col-start-2">
                                <span
                                    id="lead-classification"
                                    className="absolute -right-[0.5px] rounded-full bg-primary-200 text-primary-500 w-16 h-16 text-[35px] inline-flex items-center justify-center font-bold z-1 select-none"
                                >
                                    F
                                </span>
                            </div>
                            <div>
                                <Element
                                    id="heading-classification"
                                    is={StepHeading}
                                    text={`Select a <span class="text-primary-500">classification system</span>`}
                                />
                                <Element id="content-classification" is={StepTextContainer} canvas>
                                    <Element
                                        id="paragraph-classification-1"
                                        is={StepParagraph}
                                        text={`Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec quam lacus, tristique at <a href="#">nulla et, imperdiet molestie nibh</a>. Vestibulum mollis viverra odio vitae sodales.`}
                                    />
                                </Element>

                                <div
                                    className={clsx([
                                        "grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 mt-10.5 gap-1 xl:gap-4",
                                        enabled && "pointer-events-none",
                                    ])}
                                >
                                    <ClassificationFilter />
                                </div>
                            </div>
                        </>
                    )}

                    <div className="relative w-full  col-start-2">
                        <span
                            id="lead-6"
                            className="absolute -right-[0.5px] rounded-full bg-primary-200 text-primary-500 w-16 h-16 text-[35px] inline-flex items-center justify-center font-bold z-1 select-none"
                        >
                            {hasClassifications ? "G" : "F"}
                        </span>
                    </div>
                    <div>
                        <Element
                            id="heading-f"
                            is={StepHeading}
                            text={`Télécharger le <span class="text-primary-500">fichier de spécification</span>`}
                        />
                        <Element id="content-f" is={StepTextContainer} canvas>
                            <Element
                                id="paragraph-f-1"
                                is={StepParagraph}
                                text={`Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec quam lacus, tristique at <a href="#">nulla et, imperdiet molestie nibh</a>. Vestibulum mollis viverra odio vitae sodales.`}
                            />
                        </Element>

                        <div className="relative flex rounded-lg bg-primary-100 text-primary-500 p-5 mt-10 items-center">
                            <div id="lead-7" />
                            <div className="grid grid-cols-1 gap-2 lg:gap-8 w-full p-5">
                                <button
                                    className={clsx([
                                        "rounded-md bg-primary-500 text-white text-l inline-flex flex-col items-center justify-center font-medium px-8 py-6 shrink-0 cursor-pointer hover:bg-primary-600",
                                    ])}
                                    onClick={() => !enabled && createExport("website-export")}
                                >
                                    <Element
                                        id="button-f-1"
                                        is={StepParagraph}
                                        text={`Générer et télécharger le fichier XLSX`}
                                    />
                                    <span className="text-sm/snug text-primary-100 -mt-1">
                                        <Element
                                            id="button-f-1-subtext"
                                            is={StepParagraph}
                                            text={`Description du bouton`}
                                        />
                                    </span>
                                </button>
                                {(enabled || showStructuredExport) && (
                                    <button
                                        className={clsx([
                                            "rounded-md bg-primary-500 text-white text-l inline-flex flex-col items-center justify-center font-medium px-8 py-6 shrink-0 cursor-pointer hover:bg-primary-600",
                                        ])}
                                        onClick={() => !enabled && createExport("structured-export")}
                                    >
                                        <Element
                                            id="button-f-2"
                                            is={StepParagraph}
                                            text={`Télécharger un fichier .xlsx pour gérer mon project au quotidien`}
                                        />
                                        <span className="text-sm/snug text-primary-100 -mt-1">
                                            <Element
                                                id="button-f-2-subtext"
                                                is={StepParagraph}
                                                text={`Description du bouton`}
                                            />
                                        </span>
                                    </button>
                                )}
                                <div className="italic font-medium text-primary-500 text-sm/snug mx-6">
                                    <Element
                                        id="description-f-1"
                                        is={StepParagraph}
                                        text={`La génération peut prendre quelques minutes et s'effectuera en arrière plan et lancera
                                le téléchargement une fois terminée. <br /> Ne fermez pas l'onglet de votre navigateur
                                avant la fin de l'opération.`}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div id="lead-8" className="col-span-2 w-[1px] mx-auto items-center -mt-3" />
            </main>
        </div>
    );
};

Step1Content.craft = {
    displayName: "Content",
    rules: {
        canDrag: () => false,
        canMoveOut: () => false,
    },
    custom: {
        canDelete: false,
    },
};

export default Step1Content;
